import { Color } from '@persona-id/design-tokens';
import { findAll } from 'highlight-words-core';
import { compact, concat, uniq } from 'lodash/fp';
import { memo } from 'react';
import styled from 'styled-components';

const ExactMatchHighlight = memo(styled('mark')`
  background-color: ${Color.CoreGreen200};
  border-radius: 4px;
  padding: 0 2px;
`);

const PartialMatchHighlight = memo(styled('mark')`
  background-color: ${Color.CoreYellow200};
  border-radius: 4px;
  padding: 0 2px;
`);

interface HighlighterProps {
  children?: string;
  exactMatchWords: string[];
  partialMatchWords: string[];
}

const Highlighter = ({ children, exactMatchWords, partialMatchWords }: HighlighterProps) => {
  const exactWords = uniq(compact(exactMatchWords)) ?? [];
  const exactMatchWordsSanitized = uniq(
    compact(exactWords.map((x) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))),
  );
  partialMatchWords = uniq(compact(partialMatchWords));
  const partialMatchWordsSanitized = uniq(
    compact(partialMatchWords.map((x) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))),
  );
  const searchWords = [
    ...exactWords,
    ...exactMatchWordsSanitized,
    ...partialMatchWords,
    ...partialMatchWordsSanitized,
  ];
  const chunks = children
    ? findAll({
        autoEscape: true, // escape regex characters so words are interpreted as string literals and not regexes
        searchWords,
        textToHighlight: children,
      })
    : [];

  const setExactMatchWords = new Set(concat(exactWords, exactMatchWordsSanitized));

  const highlightedChildren = chunks.map((chunk, idx) => {
    const text = children?.substr(chunk.start, chunk.end - chunk.start);

    if (chunk.highlight) {
      if (text && setExactMatchWords.has(text)) {
        return <ExactMatchHighlight key={idx}>{text}</ExactMatchHighlight>;
      }
      return <PartialMatchHighlight key={idx}>{text}</PartialMatchHighlight>;
    }
    return <span key={idx}>{text}</span>;
  });

  return <span>{highlightedChildren}</span>;
};

export default Highlighter;
